import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { createApiInventoryModule } from "@/services/api-inventory/apiInventory";
import type { ApiEntry, InventoryItem, StoredUrl } from "@/services/api-inventory/apiInventory.types";
import type { ExcelFunction } from "@/services/custom-functions/customFunction.types";
import { createApiRegistrationModule } from "@/services/custom-functions/customFunctionRegistration";
import type { ExtendedDocument } from "@/services/open-api/openApi.types";
import { readStoredUrls } from "@/utils/utils";

export const useApiStore = defineStore("api", () => {
  const baseUrl = ref<string>(import.meta.env.VITE_API_URL || "http://localhost:8000");
  const rawUrls = ref<StoredUrl[]>(readStoredUrls());
  const inventory = ref<InventoryItem[]>([]);
  const apiEntries = ref<Map<string, ApiEntry>>(new Map());
  const cachedSchemaMap = ref<Map<string, ExtendedDocument>>(new Map());
  const isInventoryLoading = ref<boolean>(false);
  const inventoryError = ref<string | null>(null);
  const autoGenerateForm = ref<boolean>(false);

  const registrationModule = createApiRegistrationModule(apiEntries);

  const inventoryModule = createApiInventoryModule(
    baseUrl,
    rawUrls,
    inventory,
    apiEntries,
    cachedSchemaMap,
    isInventoryLoading,
    inventoryError,
    registrationModule.validateFunctionsForEntry,
    registrationModule.registerAllFunctions
  );

  const apiEntryList = computed(() =>
    Array.from(apiEntries.value.entries()).map(([url, entry]) => ({
      ...entry,
      url,
    }))
  );

  const loadingApiEntries = computed(() => apiEntryList.value.filter((entry) => entry.state === "loading"));

  const loadedApiEntries = computed(() => apiEntryList.value.filter((entry) => entry.state === "loaded"));

  const visibleApiEntries = computed(() =>
    apiEntryList.value.filter((entry) => entry.state === "loading" || entry.state === "loaded")
  );

  const registrableFunctions = computed<ExcelFunction[]>(() =>
    apiEntryList.value.flatMap((entry) => entry.registrableFunctions)
  );

  const validationMessages = computed(() =>
    apiEntryList.value
      .filter((entry) => entry.state === "error" && entry.error)
      .map((entry) => {
        const name = typeof entry.item === "object" ? entry.item.name : entry.url;
        return `${name}: ${entry.error}`;
      })
  );

  function getCachedSchema(url: string): ExtendedDocument | undefined {
    return cachedSchemaMap.value.get(url);
  }

  async function updateSelectedApis(newUrls: StoredUrl[]): Promise<void> {
    inventoryModule.setRawUrls(newUrls);
    await inventoryModule.syncUrls(newUrls);
  }

  async function initializeApiCatalog(): Promise<void> {
    await inventoryModule.loadInventory();
    await inventoryModule.syncUrls(rawUrls.value);
  }

  return {
    baseUrl,
    rawUrls,
    inventory,
    apiEntries,
    isInventoryLoading,
    inventoryError,
    autoGenerateForm,
    apiEntryList,
    loadingApiEntries,
    loadedApiEntries,
    visibleApiEntries,
    registrableFunctions,
    validationMessages,
    setRawUrls: inventoryModule.setRawUrls,
    loadInventory: inventoryModule.loadInventory,
    syncUrls: inventoryModule.syncUrls,
    getCachedSchema,
    updateSelectedApis,
    initializeApiCatalog,
  };
});
